import { useEffect, useState } from "react";
import { orders as api, type Order, type OrderStatus } from "../../api";
import { Link, useRouter } from "../../router";
import { formatCurrency } from "../portfolio/format";
import {
  instrumentLabel,
  quantityLabel,
  sideLabel,
  statusLabel,
  formatDate,
} from "./format";

const STATUS_FILTERS: { value: "" | OrderStatus; label: string }[] = [
  { value: "", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "submitted", label: "Submitted" },
  { value: "partially_filled", label: "Part-filled" },
  { value: "executed", label: "Executed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "rejected", label: "Rejected" },
];

export function OrderList() {
  const { navigate } = useRouter();
  const [items, setItems] = useState<Order[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<"" | OrderStatus>("");

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    api
      .list(status ? { status } : {})
      .then((d) => {
        if (!cancelled) setItems(d.orders);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [status]);

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <span className="eyebrow">Orders</span>
          <h1>Order management</h1>
        </div>
        <div className="page-head-actions">
          <Link to="/app/orders/new" className="btn btn-primary">
            New order
          </Link>
        </div>
      </header>

      <p className="muted">
        Manually capture buy, sell, and switch orders against any account.
        Executing an order updates the positions in the portfolio system.
      </p>

      <nav className="tabs">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value || "all"}
            type="button"
            className={status === f.value ? "tab-link active" : "tab-link"}
            onClick={() => setStatus(f.value)}
          >
            {f.label}
          </button>
        ))}
      </nav>

      {error && <div className="banner-error">{error}</div>}

      {items === null ? (
        <div className="muted">Loading…</div>
      ) : items.length === 0 ? (
        <div className="empty">
          <p>No orders {status ? `with status “${status}”` : "yet"}.</p>
        </div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Created</th>
              <th>Investor</th>
              <th>Account</th>
              <th>Side</th>
              <th>Instrument</th>
              <th className="num">Quantity</th>
              <th>Status</th>
              <th className="num">Value</th>
            </tr>
          </thead>
          <tbody>
            {items.map((o) => (
              <tr
                key={o.id}
                className="row-link"
                onClick={() => navigate(`/app/orders/${o.id}`)}
              >
                <td>{formatDate(o.created_at)}</td>
                <td className="strong">{o.investor_name}</td>
                <td>{o.account_name}</td>
                <td>
                  <span className={`pill side-${o.side}`}>{sideLabel(o.side)}</span>
                </td>
                <td>{instrumentLabel(o)}</td>
                <td className="num">
                  {quantityLabel(o.quantity_mode, o.quantity, o.account_currency)}
                </td>
                <td>
                  <span className={`pill status-${o.status}`}>
                    {statusLabel(o.status)}
                  </span>
                </td>
                <td className="num">
                  {o.executed_value != null
                    ? formatCurrency(o.executed_value, o.account_currency)
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
